import React from "react";
import {
  Building2,
  MapPin,
  Clock,
  Award,
  DollarSign,
  CheckCircle2,
  XCircle,
  Code2,
  FileText,
  Briefcase,
} from "lucide-react";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";

const ViewJobOpening = ({ open, onClose, job }) => {
  if (!job) return null;

  const footer = (
    <Button variant="secondary" size="sm" onClick={onClose} className="px-8">
      Close
    </Button>
  );

  const StatusBadge = ({ active }) => (
    <div
      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
        active
          ? "bg-green-100 text-green-700 border border-green-200"
          : "bg-gray-100 text-gray-600 border border-gray-200"
      }`}
    >
      {active ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5" />
      )}
      {active ? "Active" : "Inactive"}
    </div>
  );

  const DetailCard = ({ icon: Icon, label, value, colorClass }) => (
    <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4 transition-all hover:shadow-md hover:border-primary-100 group">
      <div
        className={`p-2.5 rounded-xl ${colorClass} transition-transform group-hover:scale-110`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
          {label}
        </p>
        <p className="text-sm font-bold text-gray-800">
          {value || "Not Specified"}
        </p>
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
            <Briefcase className="w-5 h-5" />
          </div>
          <span>Job Details</span>
        </div>
      }
      size="xl"
      footer={footer}
    >
      <div className="space-y-8 py-2">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-white shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 rounded-full blur-[100px] opacity-20 -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary-500 rounded-full blur-[80px] opacity-20 -ml-24 -mb-24"></div>

          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <StatusBadge active={job.isActive} />
              <div className="bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white/90">
                {job.type || "Full-time"}
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-black mb-2 tracking-tight">
              {job.title}
            </h1>
            <div className="flex items-center gap-2 text-gray-300 font-medium">
              <Building2 className="w-4 h-4 text-primary-400" />
              <span>{job.department}</span>
              <span className="text-white/20 mx-1">•</span>
              <MapPin className="w-4 h-4 text-secondary-400" />
              <span>{job.location}</span>
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailCard
            icon={Clock}
            label="Experience Required"
            value={job.experience}
            colorClass="bg-blue-50 text-gray-700"
          />
          <DetailCard
            icon={DollarSign}
            label="Salary Range"
            value={job.salary}
            colorClass="bg-emerald-50 text-emerald-600"
          />
          <DetailCard
            icon={Briefcase}
            label="Employment Type"
            value={job.type || "Full-time"}
            colorClass="bg-purple-50 text-purple-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Description */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                <FileText className="w-4 h-4" />
              </div>
              <h3 className="text-lg font-black text-gray-800">The Role</h3>
            </div>
            <div className="bg-gray-50/50 rounded-3xl p-6 border border-gray-100 text-gray-700 leading-relaxed whitespace-pre-wrap text-sm italic shadow-inner">
              {job.description || "No description provided for this opening."}
            </div>
          </div>

          {/* Right Column: Skills */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                <Code2 className="w-4 h-4" />
              </div>
              <h3 className="text-lg font-black text-gray-800">
                Target Skills
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(job.skills) && job.skills.length > 0 ? (
                job.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white border border-gray-100 shadow-sm text-gray-700 rounded-2xl text-xs font-bold flex items-center gap-2 transition-all hover:border-primary-300 hover:text-primary-700"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-400"></div>
                    {skill}
                  </span>
                ))
              ) : (
                <div className="text-sm text-gray-400 font-medium bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-4 w-full text-center">
                  No specific skills listed
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewJobOpening;
